import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from '@reach/router';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import Search from '@mui/icons-material/Search';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import { toast } from 'react-toastify';
import spinner from '../assign-prescriptions/images/spinner.gif';
import { FeatureGlyph } from '../features/FeatureMarkers';
import { FeaturesHelpButton } from '../features/FeaturesHelpGuide';
import ILocalAGGrid, { getDefaultColDef } from '../grid/iLocalAGGrid';
import {
  fetchAdminEntitlements,
  fetchAdminFeatureLeads,
  searchLicensingAccounts,
  setAdminEntitlement,
  displaySafeText,
  featureTitleForKey,
  getLicensingQueryAccountId,
  getLicensingQueryFeatureKey,
} from '../../utils/productLicensing';
import '../features/features.css';
import '../reports/feature-purchases/feature-purchases.css';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const isFeatureOn = (feature) =>
  !!(feature && feature.enabled === true && feature.reason !== 'geo_blocked');

const OpenRequestStatusCell = (params) => {
  const enabled = params.data && params.data.status === 'enabled';
  return (
    <span
      className={`feature-card-status ${enabled ? 'is-enabled' : 'is-open'}`}
    >
      {params.value}
    </span>
  );
};

const FeatureLicensingAdmin = ({ t }) => {
  const isIlocalUser = localStorage.getItem('isIlocalUser') === 'true';
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [openRequests, setOpenRequests] = useState([]);

  const loadAccounts = async () => {
    setAccountLoading(true);
    try {
      const data = await searchLicensingAccounts('');
      const accounts = ((data && data.accounts) || []).filter(
        (item) => item && item.id && item.name
      );
      setAccountOptions(accounts);
      const queryAccountId = getLicensingQueryAccountId();
      if (queryAccountId) {
        const match = accounts.find(
          (item) => String(item.id) === String(queryAccountId)
        );
        const account = match || {
          id: queryAccountId,
          name: queryAccountId,
        };
        setSelectedAccount({
          id: account.id,
          name: displaySafeText(account.name),
        });
        loadDetail(account.id);
      }
    } catch (_err) {
      setAccountOptions([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const loadOpenRequests = async (accountId) => {
    if (!accountId) {
      setOpenRequests([]);
      return;
    }
    try {
      const data = await fetchAdminFeatureLeads({
        status: 'open',
        accountId,
      });
      setOpenRequests((data && data.leads) || []);
    } catch (_err) {
      setOpenRequests([]);
    }
  };

  useEffect(() => {
    if (isIlocalUser) {
      loadAccounts();
    }
  }, [isIlocalUser]);

  useEffect(() => {
    if (!isIlocalUser) {
      return;
    }
    loadOpenRequests(selectedAccount && selectedAccount.id);
  }, [isIlocalUser, selectedAccount]);

  useEffect(() => {
    if (!detail) {
      return undefined;
    }
    const featureKey = getLicensingQueryFeatureKey();
    if (!featureKey) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const node = document.querySelector(
        `[data-licensing-feature="${featureKey}"]`
      );
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [detail]);

  const loadDetail = async (accountId, options) => {
    const silent = !!(options && options.silent);
    if (!silent) {
      setDetailLoading(true);
    }
    try {
      const data = await fetchAdminEntitlements(accountId);
      setDetail(data);
    } catch (err) {
      toast.error(
        (err && err.message) ||
          t('LicensingAdmin.LoadError', {
            defaultValue: 'Unable to load entitlements for this account.',
          })
      );
      if (!silent) {
        setDetail(null);
      }
    } finally {
      if (!silent) {
        setDetailLoading(false);
      }
    }
  };

  const namedAccounts = useMemo(
    () =>
      (accountOptions || [])
        .map((item) => ({
          id: item.id,
          name: displaySafeText(item.name),
        }))
        .filter((item) => item.id && item.name),
    [accountOptions]
  );

  const handleAccountChange = (_event, account) => {
    setSelectedAccount(account);
    setDetail(null);
    if (account && account.id) {
      loadDetail(account.id);
    }
  };

  const handleOpenRequest = (lead) => {
    if (!lead || !lead.account_id) return;
    const existing = namedAccounts.find((item) => item.id === lead.account_id);
    const account = existing || {
      id: lead.account_id,
      name: displaySafeText(lead.account_name, ''),
    };
    handleAccountChange(null, account);
  };

  const handleToggle = async (feature, enabled) => {
    if (!selectedAccount || savingKey) return;
    setSavingKey(feature.feature_key);
    try {
      const openLead = ((detail && detail.leads) || []).find(
        (lead) =>
          lead.feature_key === feature.feature_key && lead.status === 'open'
      );
      await setAdminEntitlement({
        account_id: selectedAccount.id,
        feature_key: feature.feature_key,
        enabled,
        lead_id: openLead ? openLead.id : undefined,
      });
      toast.success(
        enabled
          ? t('LicensingAdmin.Enabled', {
              defaultValue: 'Feature enabled for this account.',
            })
          : t('LicensingAdmin.Disabled', {
              defaultValue: 'Feature disabled for this account.',
            })
      );
      await loadDetail(selectedAccount.id, { silent: true });
      await loadOpenRequests(selectedAccount.id);
    } catch (err) {
      toast.error(
        (err && err.message) ||
          t('LicensingAdmin.SaveError', {
            defaultValue: 'Unable to update this feature.',
          })
      );
    } finally {
      setSavingKey('');
    }
  };

  const entitlements = useMemo(
    () => (detail && detail.entitlements) || [],
    [detail]
  );
  const leads = (detail && detail.leads) || [];
  const enabledCount = entitlements.filter(isFeatureOn).length;
  const openLeadCount = leads.filter((lead) => lead.status === 'open').length;

  const statusLabel = (feature) => {
    if (feature.included_in_base) {
      return t('LicensingAdmin.Included', { defaultValue: 'Included in base' });
    }
    if (feature.reason === 'geo_blocked') {
      return t('Features.StatusGeo', { defaultValue: 'Not available here' });
    }
    if (isFeatureOn(feature)) {
      return t('Features.StatusEnabled', { defaultValue: 'Enabled' });
    }
    return t('Features.StatusLocked', { defaultValue: 'Not enabled' });
  };

  const statusClass = (feature) => {
    if (feature.reason === 'geo_blocked') return 'is-geo';
    if (isFeatureOn(feature) || feature.included_in_base) return 'is-enabled';
    return 'is-locked';
  };

  const leadStatusLabel = (status) => {
    if (status === 'open') {
      return t('LicensingAdmin.LeadOpen', { defaultValue: 'Awaiting follow-up' });
    }
    if (status === 'enabled') {
      return t('LicensingAdmin.LeadEnabled', { defaultValue: 'Enabled' });
    }
    return displaySafeText(status, '—');
  };

  const openRequestColumnDefs = useMemo(
    () => [
      {
        field: 'created_on',
        colId: 'created_on',
        headerName: t('LicensingAdmin.When', { defaultValue: 'When' }),
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 180,
      },
      {
        field: 'requested_by',
        colId: 'requested_by',
        headerName: t('FeatureRequests.RequestedBy', {
          defaultValue: 'Requested by',
        }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.requested_by, '—'),
        minWidth: 160,
      },
      {
        field: 'feature_title',
        colId: 'feature_title',
        headerName: t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
        valueGetter: (params) => {
          const row = params.data || {};
          return displaySafeText(
            row.feature_title,
            featureTitleForKey(row.feature_key)
          );
        },
        minWidth: 180,
      },
      {
        field: 'account_name',
        colId: 'account_name',
        headerName: t('FeaturePurchases.Account', { defaultValue: 'Account' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.account_name, '—'),
        minWidth: 180,
      },
      {
        field: 'location_name',
        colId: 'location_name',
        headerName: t('FeatureRequests.Location', { defaultValue: 'Location' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.location_name, '—'),
        minWidth: 180,
      },
      {
        field: 'status',
        colId: 'status',
        headerName: t('LicensingAdmin.Status', { defaultValue: 'Status' }),
        valueGetter: (params) =>
          leadStatusLabel(params.data && params.data.status),
        cellRenderer: OpenRequestStatusCell,
        minWidth: 160,
      },
    ],
    [t]
  );

  if (!isIlocalUser) {
    return (
      <div className="licensing-admin">
        <p className="licensing-empty">
          {t('LicensingAdmin.Forbidden', {
            defaultValue: 'This page is only available to iLocal members.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="licensing-admin">
      {savingKey ? (
        <div className="licensing-progress-overlay" aria-busy="true">
          <img
            className="licensing-progress-overlay-image"
            src={spinner}
            alt={t('LicensingAdmin.Saving', { defaultValue: 'Saving' })}
          />
        </div>
      ) : null}
      <div className="features-hero">
        <p className="features-hero-eyebrow">
          {t('LicensingAdmin.Eyebrow', { defaultValue: 'Offline enablement' })}
        </p>
        <p className="features-intro">
          {t('LicensingAdmin.Intro', {
            defaultValue:
              'Enable or disable licensed features for a pharmacy account after the offline sale. Enablement applies to all locations on that account. This page does not collect payment.',
          })}
        </p>
        <div className="licensing-admin-links">
          <FeaturesHelpButton t={t} />
          <Link to="/home/manage/feature-catalog" className="licensing-admin-report-link">
            {t('LicensingAdmin.ViewCatalog', {
              defaultValue: 'Manage feature catalog',
            })}
          </Link>
          <Link to="/home/reports/feature-requests" className="licensing-admin-report-link">
            {t('LicensingAdmin.ViewRequests', {
              defaultValue: 'View feature requests',
            })}
          </Link>
          <Link to="/home/reports/feature-purchases" className="licensing-admin-report-link">
            {t('LicensingAdmin.ViewReport', {
              defaultValue: 'View Feature Purchases report',
            })}
          </Link>
          <Link
            to="/home/reports/feature-enablement-history"
            className="licensing-admin-report-link"
          >
            {t('LicensingAdmin.ViewHistory', {
              defaultValue: 'View enablement history',
            })}
          </Link>
        </div>
      </div>

      <div className="licensing-panel licensing-open-requests">
        <div className="licensing-panel-head">
          <h2>
            {t('LicensingAdmin.OpenRequests', {
              defaultValue: 'Open feature requests',
            })}
          </h2>
          {selectedAccount && openRequests.length > 0 ? (
            <span className="feature-card-status is-open">
              {t('LicensingAdmin.OpenLeadCount', {
                defaultValue: '{{count}} open',
                count: openRequests.length,
              })}
            </span>
          ) : null}
        </div>
        {!selectedAccount ? (
          <div className="licensing-empty-card is-compact">
            <ForumOutlined />
            <h3>
              {t('LicensingAdmin.SelectPromptTitle', {
                defaultValue: 'Select a pharmacy account',
              })}
            </h3>
            <p>
              {t('LicensingAdmin.OpenRequestsNeedAccount', {
                defaultValue:
                  'Open requests and counts are shown for the pharmacy selected below.',
              })}
            </p>
          </div>
        ) : openRequests.length === 0 ? (
          <div className="licensing-empty-card is-compact">
            <ForumOutlined />
            <h3>
              {t('LicensingAdmin.NoOpenRequestsTitle', {
                defaultValue: 'No open requests',
              })}
            </h3>
            <p>
              {t('LicensingAdmin.NoOpenRequestsForAccount', {
                defaultValue: 'This pharmacy has no open feature requests.',
              })}
            </p>
          </div>
        ) : (
          <ILocalAGGrid
            className="ag-theme-alpine feature-purchases-grid licensing-open-requests-grid"
            columnDefs={openRequestColumnDefs}
            defaultColDef={getDefaultColDef()}
            rowModelType="clientSide"
            rowData={openRequests}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={48}
            overlayNoRowsMessage={t('LicensingAdmin.NoOpenRequestsTitle', {
              defaultValue: 'No open requests',
            })}
            onRowClicked={(event) => {
              if (event && event.data) {
                handleOpenRequest(event.data);
              }
            }}
            getRowId={(params) => {
              const row = params.data || {};
              if (row.id) {
                return String(row.id);
              }
              return [
                row.account_id,
                row.feature_key,
                row.created_on,
                row.contact_email,
              ].join('|');
            }}
          />
        )}
      </div>

      <div className="licensing-picker-card">
        <label className="licensing-admin-search-label" htmlFor="licensing-account-search">
          {t('LicensingAdmin.SearchAccount', {
            defaultValue: 'Search account',
          })}
        </label>
        <Autocomplete
          id="licensing-account-search"
          className="licensing-account-autocomplete"
          fullWidth
          options={namedAccounts}
          loading={accountLoading}
          value={selectedAccount}
          autoHighlight
          openOnFocus
          clearOnBlur={false}
          getOptionLabel={(option) => displaySafeText(option && option.name, '')}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          filterOptions={(options, state) => {
            const query = String(state.inputValue || '')
              .trim()
              .toLowerCase();
            if (!query) return options;
            return options.filter((option) =>
              String(option.name || '')
                .toLowerCase()
                .includes(query)
            );
          }}
          onChange={handleAccountChange}
          noOptionsText={t('LicensingAdmin.NoAccounts', {
            defaultValue: 'No accounts found',
          })}
          ListboxProps={{
            style: { maxHeight: 280 },
          }}
          componentsProps={{
            paper: {
              className: 'licensing-account-menu',
            },
            popper: {
              style: { zIndex: 1300 },
            },
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('LicensingAdmin.SearchPlaceholder', {
                defaultValue: 'Search accounts...',
              })}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#64748b' }} />
                    {params.InputProps.startAdornment}
                  </InputAdornment>
                ),
                endAdornment: (
                  <React.Fragment>
                    {accountLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        {selectedAccount && !detailLoading && detail ? (
          <div className="licensing-account-summary">
            <span className="licensing-account-summary-icon">
              <StorefrontOutlined />
            </span>
            <div>
              <strong>{displaySafeText(selectedAccount.name, '—')}</strong>
              <p>
                {t('LicensingAdmin.SummaryLine', {
                  defaultValue:
                    '{{enabled}} of {{total}} features enabled · {{leads}} expert requests',
                  enabled: enabledCount,
                  total: entitlements.length,
                  leads: openLeadCount,
                })}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {detailLoading && (
        <div className="licensing-admin-loading">
          <CircularProgress size={28} />
        </div>
      )}

      {!selectedAccount && !detailLoading ? (
        <div className="licensing-empty-card">
          <StorefrontOutlined />
          <h3>
            {t('LicensingAdmin.SelectPromptTitle', {
              defaultValue: 'Select a pharmacy account',
            })}
          </h3>
          <p>
            {t('LicensingAdmin.SelectPrompt', {
              defaultValue:
                'Search for a pharmacy to enable or disable licensed features after the offline sale.',
            })}
          </p>
        </div>
      ) : null}

      {!detailLoading && selectedAccount && entitlements.length > 0 && (
        <div className="licensing-feature-list">
          <h2 className="licensing-section-title">
            {t('LicensingAdmin.FeaturesHeading', {
              defaultValue: 'Licensed features',
            })}
          </h2>
          {entitlements.map((feature) => {
            const on = isFeatureOn(feature);
            const geoBlocked = feature.reason === 'geo_blocked';
            const isSaving = savingKey === feature.feature_key;
            const isTarget =
              getLicensingQueryFeatureKey() === feature.feature_key;
            return (
              <div
                className={`licensing-feature-card ${statusClass(feature)}${
                  isTarget ? ' is-target' : ''
                }`}
                data-licensing-feature={feature.feature_key}
                key={feature.feature_key}
              >
                <div className="licensing-feature-card-main">
                  <span className={`feature-card-icon ${statusClass(feature)}`}>
                    <FeatureGlyph featureKey={feature.feature_key} />
                  </span>
                  <div>
                    <div className="licensing-feature-card-header">
                      <h3>{feature.title}</h3>
                      <span className={`feature-card-status ${statusClass(feature)}`}>
                        {statusLabel(feature)}
                      </span>
                    </div>
                    <p>{feature.tooltip}</p>
                    <p className="licensing-feature-hint">
                      {geoBlocked
                        ? t('Features.GeoBlockedDetail', {
                            defaultValue:
                              'This location cannot offer this feature because of local rules.',
                          })
                        : feature.included_in_base
                          ? t('LicensingAdmin.IncludedHint', {
                              defaultValue:
                                'This capability is included in the base product.',
                            })
                          : t('LicensingAdmin.ToggleHint', {
                              defaultValue:
                                'Turn this on after the offline sale so every location on this account can use it on Deliver. Nothing is charged here.',
                            })}
                    </p>
                  </div>
                </div>
                <div className="licensing-feature-toggle">
                  <span>
                    {isSaving
                      ? t('LicensingAdmin.Saving', { defaultValue: 'Saving' })
                      : on
                        ? t('Features.StatusEnabled', { defaultValue: 'Enabled' })
                        : t('Features.StatusLocked', { defaultValue: 'Not enabled' })}
                  </span>
                  <div className="licensing-feature-toggle-control">
                    {isSaving ? (
                      <CircularProgress size={18} thickness={5} />
                    ) : null}
                    <Switch
                      checked={on}
                      disabled={
                        feature.included_in_base ||
                        geoBlocked ||
                        !!savingKey
                      }
                      onChange={(_event, checked) =>
                        handleToggle(feature, checked)
                      }
                      color="primary"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default withTranslation()(FeatureLicensingAdmin);



// WEBPACK FOOTER //
// ./src/components/feature-licensing-admin/index.js